const MESSAGE_ACTION_TYPES = ['set_message_type', 'set_message_body', 'set_attachments'];

export function extractMacroMessageEffects(actions, ticketData, fallbackCommentIsInternal = false) {
    if (!Array.isArray(actions)) {
        return {
            hasMessageText: false,
            messageBody: '',
            messageIsInternal: Boolean(ticketData?.is_internal ?? fallbackCommentIsInternal),
            attachments: null,
        };
    }

    let body = null;
    let isInternal = ticketData?.is_internal ?? fallbackCommentIsInternal;
    let attachments = null;

    actions.forEach((action) => {
        if (action.type === 'set_message_body' && action.body != null) {
            body = action.body;
        }
        if (action.type === 'set_message_type' && action.is_internal != null) {
            isInternal = !!action.is_internal;
        }
        if (action.type === 'set_attachments' && action.attachments?.length) {
            attachments = action.attachments;
        }
    });

    const hasMessageText = typeof body === 'string' ? body.trim().length > 0 : body != null;

    return {
        hasMessageText,
        messageBody: body || '',
        messageIsInternal: isInternal,
        attachments,
    };
}

function syncLegacyStatusFromStatusId(next, statusId, ticketStatuses) {
    if (!statusId || !Array.isArray(ticketStatuses) || !ticketStatuses.length) {
        return next;
    }
    const st = ticketStatuses.find((s) => Number(s.id) === Number(statusId));
    if (!st?.name) return next;
    if (st.name === 'Resolvido') return { ...next, status: 3 };
    if (st.name === 'Cancelado') return { ...next, status: 4 };
    if (st.name === 'Aberto') return { ...next, status: 1 };
    return next;
}

export function applyMacroActionsToTicketData(ticketData, macro, options = {}) {
    const { skipMessageActions = false, ticketStatuses = [] } = options;
    const actions = macro?.actions;

    if (!ticketData || !Array.isArray(actions) || !actions.length) {
        return { ticketData: ticketData || {}, messageEffects: null };
    }

    let next = { ...ticketData };

    actions.forEach((action) => {
        const type = action?.type;
        if (!type) return;
        if (skipMessageActions && MESSAGE_ACTION_TYPES.includes(type)) return;

        switch (type) {
            case 'set_ticket_type':
                if (Object.prototype.hasOwnProperty.call(action, 'value')) {
                    next.is_internal = !!action.value;
                }
                break;
            case 'set_status':
                if (action.status_id) {
                    next.status_id = action.status_id;
                    next = syncLegacyStatusFromStatusId(next, action.status_id, ticketStatuses);
                }
                break;
            case 'set_service':
                if (Object.prototype.hasOwnProperty.call(action, 'service_id')) {
                    next.service_id = action.service_id || null;
                }
                break;
            case 'set_category':
                if (Object.prototype.hasOwnProperty.call(action, 'category')) {
                    next.category = action.category ?? '';
                }
                break;
            case 'set_urgency':
                if (Object.prototype.hasOwnProperty.call(action, 'priority')) {
                    next.priority = Number(action.priority);
                }
                break;
            case 'set_responsible':
                if (Object.prototype.hasOwnProperty.call(action, 'assigned_to')) {
                    next.assigned_to = action.assigned_to || '';
                }
                break;
            case 'set_subject':
                if (Object.prototype.hasOwnProperty.call(action, 'title')) {
                    next.title = action.title ?? '';
                }
                break;
            case 'add_cc': {
                const cc = Array.isArray(next.cc_users) ? [...next.cc_users] : [];
                const addIds = action.user_ids ?? [];
                next.cc_users = [...new Set([...cc, ...addIds])];
                const ccEmails = Array.isArray(next.cc_emails) ? [...next.cc_emails] : [];
                const addEmails = action.cc_emails ?? [];
                next.cc_emails = [...new Set([...ccEmails, ...addEmails])];
                break;
            }
            case 'remove_cc': {
                const cc = Array.isArray(next.cc_users) ? next.cc_users : [];
                const removeIds = action.user_ids ?? [];
                next.cc_users = cc.filter((uid) => !removeIds.includes(uid));
                const ccEmails = Array.isArray(next.cc_emails) ? next.cc_emails : [];
                const removeEmails = action.cc_emails ?? [];
                next.cc_emails = ccEmails.filter((em) => !removeEmails.includes(em));
                break;
            }
            default:
                break;
        }
    });

    const messageEffects = extractMacroMessageEffects(
        actions,
        next,
        options.commentIsInternal
    );

    return {
        ticketData: next,
        messageEffects: messageEffects.hasMessageText || messageEffects.attachments
            ? messageEffects
            : null,
    };
}

export function applyMacroToTicketDraft(ticketData, macroId, macros, options = {}) {
    const macro = (macros || []).find((m) => m.id === Number(macroId) || m.id === macroId);
    if (!macro) {
        return { ticketData, messageEffects: null };
    }

    const preview = extractMacroMessageEffects(
        macro.actions,
        ticketData,
        options.commentIsInternal
    );

    const { ticketData: updated, messageEffects } = applyMacroActionsToTicketData(ticketData, macro, {
        skipMessageActions: preview.hasMessageText,
        ticketStatuses: options.ticketStatuses,
        commentIsInternal: options.commentIsInternal,
    });

    const effects = preview.hasMessageText
        ? preview
        : messageEffects;

    return { ticketData: updated, messageEffects: effects };
}
